import { type VariantProps } from 'class-variance-authority';
import React from 'react';
import { ChevronRight, ChevronLeft } from 'react-bootstrap-icons';
import { cn } from '@utils';
import { Spinner } from '@atoms/Spinner/Spinner';
import { buttonStyles } from './ButtonVariants';

export type ButtonProps = VariantProps<typeof buttonStyles> &
  React.ButtonHTMLAttributes<HTMLButtonElement> & {
    loading?: boolean;
    icon?: 'left' | 'right' | null;
    label?: string | React.ReactNode;
    isExternalLink?: boolean;
    href?: string;
  };

export const Button: React.FC<ButtonProps> = ({
  label,
  variant,
  color,
  size,
  loading,
  disabled,
  className,
  icon,
  onClick,
  isExternalLink,
  href,
  ...props
}: ButtonProps) => {
  const buttonClassName = cn(
    buttonStyles({ variant, color, size }),
    className,
    { 'cursor-not-allowed opacity-50': disabled || loading },
    disabled
      ? 'bg-gray-300 hover:!bg-gray-300 focus:!bg-gray-300 active:!border-none active:!bg-gray-300'
      : '',
  );

  const handleClick = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    if (variant === 'navigation') {
      if (isExternalLink && href !== undefined) {
        window.location.href = href;
      }
    }
    if (onClick) {
      onClick(e);
    }
  };

  return (
    <button
      disabled={disabled || loading}
      className={buttonClassName}
      type="button"
      onClick={handleClick}
      {...props}
    >
      {icon === 'left' && variant === 'navigation' && !loading ? (
        <ChevronLeft />
      ) : null}
      <span className="flex items-center justify-center whitespace-nowrap">
        {loading ? <Spinner /> : label}
      </span>
      {icon === 'right' && variant === 'navigation' && !loading ? (
        <ChevronRight />
      ) : null}
    </button>
  );
};

export default Button;
